import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Attention, Button, Typography } from "@optimizely/axiom";
import { useForm } from "react-hook-form";
import { datadogRum } from "@datadog/browser-rum";
import classnames from "classnames";

import { SidebarFooter } from "../Sidebar/SidebarFooter";
import { RoleAttributeSelections } from "./RoleAttributeSelections";
import LimitByRole from "../../components/LimitByRole/LimitByRole";
import { AdminCenterScopeDropdowns } from "./AdminCenterScopeDropdowns";
import { ProductDropdown } from "../ProductDropdown/ProductDropdown";
import { RoleType, RoleTypeSelection } from "./RoleTypeSelection";
import { RolePermissionsDetails } from "./RolePermissionsDetails";

import { IOrganizationProductInstance, OrganizationProduct } from "../../../domain/OrganizationProduct";
import { Role } from "../../../domain/Role";
import { IProductAttribute } from "../../../domain/ProductAttribute.interface";
import { RoleAttribute } from "../../../domain/RoleAttribute.interface";
import {
    CUSTOMER_DEFINED_PRODUCT_ROLE,
    EXP_PERMISSIONS_KEY,
    SYSTEM_ROLE_TYPE,
    ADMINCENTER_PRODUCT_ID,
    ATTRIBUTE_ROLES,
    ALL_INSTANCES_OPTION
} from "../../../constants/constants";
import { ANALYTICS_FLOWS } from "../../../constants/analytics-constants";
import { Flags } from "../../../feature-flags/flags";

import { useAttributes } from "../../../hooks/useAttributes/useAttributes";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { emitToast } from "../../../lib/toaster-utils";
import { useAnalyticsTracking } from "../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { getCreateCustomRolePayload } from "../../../lib/analytics-helpers";

import styles from "./RoleForm.module.scss";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";

export interface IRoleFormValues {
    attributes?: IProductAttribute[];
    description?: string;
    instances: IOrganizationProductInstance[];
    name: string;
    product: OrganizationProduct;
    roleType?: RoleType;
}

type RoleFormProps = {
    allRoles?: Role[];
    onClose: () => void;
    onSubmit: (roleDetails: IRoleFormValues) => Promise<any>;
    onSuccess: ({ role }: { role: Role }) => void;
    products: OrganizationProduct[];
    role?: Role;
};

export const RoleForm = ({ allRoles: roles, onClose, onSubmit, onSuccess, products, role }: RoleFormProps) => {
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [adminCenterScopes, setAdminCenterScopes] = useState<{ name: string; value: string }[]>();
    const [selectedAttributes, setSelectedAttributes] = useState<any[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<OrganizationProduct | null>(null);
    const [selectedInstances, setSelectedInstances] = useState<IOrganizationProductInstance[]>([]);
    const { enabled: attributePermissionsEnabled } = useFeatureFlag(Flags.USE_ATTRIBUTE_PERMISSIONS);
    const { enabled: disableCustomRoleCreation, variables: disabledProductsRoleCreation } = useFeatureFlag(
        Flags.DISABLE_CUSTOM_ROLE_CREATION
    );

    const { sendTrackEvent } = useAnalyticsTracking();

    const productsThatAllowCustomRoles = useMemo(
        () =>
            products.filter((p) => {
                const disabledProduct =
                    disableCustomRoleCreation &&
                    (disabledProductsRoleCreation as any)["disabled_products"].products
                        .map((i: any) => i.id)
                        .includes(p.id);

                return p.allowCustomerDefinedRoles && !disabledProduct;
            }),
        [products, disabledProductsRoleCreation, disableCustomRoleCreation]
    );

    const currentRole = useRef(role);
    const [attributesReady, setAttributesReady] = useState(false);

    const isExp = selectedProduct?.id === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;
    const isAdminCenter = selectedProduct?.id === ADMINCENTER_PRODUCT_ID;
    const { attributes } = useAttributes({
        isActive: true,
        key: isExp ? EXP_PERMISSIONS_KEY : undefined,
        productId: selectedProduct?.id
    });

    const {
        register,
        handleSubmit,
        setError,
        getValues,
        clearErrors,
        setValue,
        formState: { isValid, errors },
        watch
    } = useForm<IRoleFormValues>({
        mode: "onChange",
        defaultValues: {
            name: role?.name,
            description: role?.description
        }
    });
    const roleType = watch("roleType");

    const appliesToAllInstances = ({ role }: { role?: Role }) => {
        return [CUSTOMER_DEFINED_PRODUCT_ROLE, SYSTEM_ROLE_TYPE].indexOf(role?.roleType || "") > -1;
    };

    useEffect(() => {
        if (!role) {
            return;
        }
        setValue("name", role.name);
        setValue("description", role.description);
        const product = products.find((p) => p.id === role.productId)!;
        setSelectedProduct(product);
    }, [products, role, setValue]);

    useEffect(() => {
        if (role !== currentRole.current) {
            currentRole.current = role;
            setAttributesReady(false);
            setEditing(false);
        }
    }, [role]);

    useEffect(() => {
        if (editing && role) {
            setSelectedAttributes(role.attributes);
            setAttributesReady(true);
        } else {
            setSelectedAttributes([]);
            setAttributesReady(false);
        }
    }, [editing, role]);

    useEffect(() => {
        clearErrors("name");
    }, [clearErrors, role?.id]);

    const productDetails = role ? products?.find((p) => p.id === role.productId) : null;

    const adminCenterProductIndex = products.findIndex((p) => p.id === ADMINCENTER_PRODUCT_ID);
    if (!attributePermissionsEnabled && adminCenterProductIndex !== -1) {
        // Remove admin center product from the dropdown if  attribute based permissions feature flag is turned off.
        products.splice(adminCenterProductIndex, 1);
    }

    const adaptDuplicateRoleError = ({ field, message }: { field: any; message: any }) => {
        if (field === "Name, ProductId") {
            return {
                field: "name",
                message: "A role with this name already exists for this product."
            };
        }
        return { field, message };
    };

    const saveRole = (roleDetails: IRoleFormValues) => {
        onSubmit(roleDetails)
            .then((newRole: Role) => {
                let instanceIds: string[] = [];
                let instanceNames: string[] = [];

                roleDetails.instances?.forEach((instance) => {
                    instanceIds.push(instance.id);
                    instanceNames.push(instance.name);
                });
                const payload = getCreateCustomRolePayload({
                    roleId: newRole.id,
                    roleName: roleDetails.name,
                    productId: roleDetails.product.id,
                    roleProductName: roleDetails.product.name,
                    instanceIds: instanceIds,
                    instanceNames: instanceNames,
                    flow: ANALYTICS_FLOWS.ROLES_PAGE
                });
                sendTrackEvent(payload);
                emitToast({ message: `Custom role ${editing ? "saved" : "created"} successfully` });
                setLoading(false);
                onSuccess({ role: newRole });
            })
            .catch((e) => {
                const errors = Array.isArray(e) ? e : [e];
                errors.forEach((error: any) => {
                    const { field, message } = error || {};
                    if (!field) {
                        datadogRum.addError(e);
                        setFormError(message);
                    } else {
                        const { field: adaptedField, message: adaptedMessage } = adaptDuplicateRoleError({
                            field,
                            message
                        });
                        setError(adaptedField.toLowerCase(), {
                            type: `invalid${adaptedField}`,
                            message: adaptedMessage
                        });
                    }
                });
                setLoading(false);
            });
    };
    const isCmpSelected = selectedProduct?.id === process.env.REACT_APP_CMP_PRODUCT_ID;

    const handleFormSubmission = () => {
        setLoading(true);
        const { name, description, roleType } = getValues();
        const roleDetails: IRoleFormValues = {
            name,
            description,
            product: selectedProduct!,
            instances: selectedInstances,
            roleType
        };

        if (selectedAttributes.length !== 1 || selectedAttributes[0] !== "N/A") {
            /*
             * When duplicating a role - the role is the GET which only has `values` but the post requires
             * `selectedValues`. Additionally, the `key` value which is required might be missing so here
             * we try to find the corresponding key so the call doesn't fail for the invalid payloads.
             */
            if (selectedAttributes?.some((attr) => !!attr.values)) {
                // @ts-ignore
                roleDetails.attributes = selectedAttributes
                    .filter((a) => !!a.values)
                    .map((attr) => {
                        const correspondingKey = attributes?.find((attribute) => {
                            return attr.values?.every((value: string) => attribute.values.indexOf(value) > -1);
                        })?.key;
                        const { id, key, values: selectedValues } = attr;

                        return {
                            id,
                            key: key || correspondingKey,
                            selectedValues,
                            scopes: adminCenterScopes || []
                        };
                    });
            } else {
                roleDetails.attributes = selectedAttributes;
            }
        }
        saveRole(roleDetails);
    };

    const descriptionFieldClasses = classnames("oui-text-input", styles["role-form__description"], {
        "oui-form-bad-news": !!errors.description
    });

    const onScopeSelectionsChanged = useCallback((scopes: { name: string; value: string }[]) => {
        setAdminCenterScopes(scopes);
    }, []);

    const disableSubmit = () => {
        const explicitRoleSelectionInvalidForm =
            !roleType || (roleType !== CUSTOMER_DEFINED_PRODUCT_ROLE && !selectedInstances.length);
        const invalidFormSelections = explicitRoleSelectionInvalidForm;

        return loading || invalidFormSelections || !isValid;
    };

    const nameFieldClasses = classnames("oui-text-input", {
        "oui-form-bad-news": !!errors.name
    });

    const nameLabelClasses = classnames("oui-label push--top", {
        "oui-form-bad-news": !!errors.name
    });

    const handleAttributeChange = ({
        id,
        key,
        preMappedValues,
        values,
        clearOtherAttributes = false
    }: {
        id?: string;
        key?: string;
        preMappedValues?: boolean;
        values: string[] | RoleAttribute[];
        clearOtherAttributes?: boolean;
    }) => {
        if (clearOtherAttributes) {
            setSelectedAttributes([{ id, key, selectedValues: values, values }]);
        }
        if (preMappedValues) {
            setSelectedAttributes(values);
            return;
        }
        if (!id) {
            setSelectedAttributes(values);
            return;
        }
        const updated = [...selectedAttributes];
        const keyIndex = updated.findIndex((i) => i.id === id);
        const attributeValue = { id, key, selectedValues: values, values };
        if (keyIndex === -1) {
            updated.push(attributeValue);
        } else if (!values?.length) {
            // attribute completely removed
            updated.splice(keyIndex, 1);
        } else {
            updated[keyIndex] = attributeValue;
        }
        setSelectedAttributes(updated);
    };

    const getRoleInstanceDetails = ({ role }: { role: Role }) => {
        const instances = products.flatMap((p) => p.instances);
        return role.targetIds.map((id) => {
            return instances.find((i) => i.id === id);
        });
    };

    const selectedInstanceTarget = editing || !role ? selectedInstances : getRoleInstanceDetails({ role: role! });
    const instanceSelectionsText = appliesToAllInstances({ role })
        ? ALL_INSTANCES_OPTION.nickname
        : selectedInstanceTarget?.map((i) => i?.nickname || i?.name).join(", ");

    const [expRolePermissionValue] = isExp
        ? role?.attributes?.find((attr) => attr.key === "permissions")?.values || []
        : [];

    const roleScopes = useMemo(() => (role?.attributes.length ? role.attributes[0].scopes : []), [role]);
    const scopes = Array.from(
        new Map(
            role?.attributes
                .flatMap((att) => att.scopes)
                .filter((i) => !!i)
                .map((item) => [item!["value"], item])
        ).values()
    );

    const handleRoleTypeChange = ({ roleType: selectedType }: { roleType: RoleType }) => {
        if (roleType !== selectedType) {
            setValue("instances", []);
            setValue("roleType", selectedType);
        }
    };

    const editButtonHoverTipMessage =
        role?.roleType === SYSTEM_ROLE_TYPE
            ? "System roles cannot be edited"
            : role?.isSystemGeneratedRole
            ? "System generated roles cannot be edited"
            : "Edit";

    return (
        <form onSubmit={handleSubmit(handleFormSubmission)} className={styles["role-form"]}>
            <div className="soft-quad--sides" id={styles["role-form-content"]}>
                {!role && editing && (
                    <Typography type="header4">
                        <span style={{ fontWeight: 300 }}>Role Details</span>
                    </Typography>
                )}
                {formError && (
                    <Attention alignment="left" className="push--top push--bottom" type="bad-news">
                        {formError}
                    </Attention>
                )}
                <label className={nameLabelClasses} htmlFor="user-group-name">
                    Role Name
                    {!role && editing && <span aria-label="(required)" className="oui-label--required" />}
                </label>
                {role && !editing ? (
                    <Typography type="body" className="label--disabled">
                        {role?.name}
                    </Typography>
                ) : (
                    <>
                        <input
                            aria-describedby="role-name"
                            id="role-name"
                            className={nameFieldClasses}
                            placeholder="Role Name"
                            type="text"
                            {...register("name")}
                        />
                        {errors.name && (
                            <span className="oui-form-note form-note--bad-news" id="role-name-error">
                                {errors.name.message}
                            </span>
                        )}
                    </>
                )}

                <label className="oui-label push-double--top" htmlFor="role-description">
                    Description
                </label>
                {role && !editing ? (
                    <Typography type="body" className="label--disabled">
                        {role?.description}
                    </Typography>
                ) : (
                    <>
                        <textarea
                            rows={5}
                            aria-describedby="role-description"
                            className={descriptionFieldClasses}
                            placeholder="Add a description to help admins/users understand what the role is for"
                            id="role-description"
                            {...register("description")}
                        />
                        {errors.description && (
                            <span className="oui-form-note form-note--bad-news" id="role-name-error">
                                {errors.description.message}
                            </span>
                        )}
                    </>
                )}
                {role && !editing ? (
                    <Typography type="body" className="label--disabled">
                        {productDetails?.name}
                    </Typography>
                ) : (
                    <ProductDropdown
                        fullWidth
                        onChange={({ product }) => setSelectedProduct(product)}
                        products={productsThatAllowCustomRoles}
                        white
                        value={selectedProduct?.id || "Select product..."}
                        tooltipMessage="Not all products allow custom roles"
                        disabled={editing}
                    />
                )}
                {role && !editing && (
                    <RolePermissionsDetails
                        role={role}
                        scopes={scopes}
                        isExp={isExp}
                        instanceSelectionsText={instanceSelectionsText}
                        products={products}
                    />
                )}
                {(editing || !role) && (
                    <>
                        <RoleTypeSelection
                            className="push-triple--top"
                            disabled={!!role || !selectedProduct}
                            handleRoleTypeChange={handleRoleTypeChange}
                            value={role?.roleType as RoleType}
                            handleCmpRoleSelection={handleAttributeChange}
                            // explicitRoleTypeSelection={true}
                            selectedInstances={selectedInstances}
                            selectedProduct={selectedProduct}
                            setSelectedInstances={setSelectedInstances}
                            editing={editing}
                            role={role}
                            selectedInstanceTarget={selectedInstanceTarget}
                        />
                        {isAdminCenter && attributePermissionsEnabled && (
                            <AdminCenterScopeDropdowns
                                products={products}
                                onSelectionsChanged={onScopeSelectionsChanged}
                                scopes={roleScopes}
                            />
                        )}
                        {!editing || attributesReady ? (
                            <RoleAttributeSelections
                                productId={selectedProduct?.id}
                                instanceId={selectedInstances?.length ? selectedInstances[0].id : undefined}
                                onChange={handleAttributeChange}
                                roles={roles || []}
                                hideDuplicateRole={
                                    editing || (isAdminCenter && attributePermissionsEnabled) || isCmpSelected
                                }
                                selectedAttributes={selectedAttributes}
                                rolePermissionValue={expRolePermissionValue}
                            />
                        ) : (
                            <LoadingIndicator />
                        )}
                    </>
                )}
            </div>
            <LimitByRole action={role ? ATTRIBUTE_ROLES.ROLES.UPDATE : ATTRIBUTE_ROLES.ROLES.CREATE} mode="hide">
                <SidebarFooter onCancel={onClose}>
                    {role && !editing ? (
                        <Button
                            key="delete-role"
                            isDisabled={role?.roleType === SYSTEM_ROLE_TYPE || !!role?.isSystemGeneratedRole}
                            onClick={() => setEditing(true)}
                            // eslint-disable-next-line react/style-prop-object
                            style="highlight"
                            title={editButtonHoverTipMessage}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Button
                            key="save-role-button"
                            isDisabled={disableSubmit()}
                            // eslint-disable-next-line react/style-prop-object
                            style="highlight"
                            isSubmit
                            loadingText="Saving"
                            isLoading={loading}
                        >
                            Save
                        </Button>
                    )}
                </SidebarFooter>
            </LimitByRole>
        </form>
    );
};

RoleForm.displayName = "RoleForm";
